import React from "react";
import { useSelector } from "react-redux";
import { hasPermission, hasAnyPermission } from "../../utils/roleBasedAccess";
import type { User, UserPermissions } from "../../utils/roleBasedAccess";
import "./PermissionWrapper.css";

export interface PermissionWrapperProps {
  /** Required permission code (e.g. role.create, user.update) */
  permission?: string;
  /** Alternative: require any of these permission codes */
  anyOfPermissions?: string[];
  /** If true, hide the child when no permission. If false, show disabled. Default: false */
  hideWhenDisabled?: boolean;
  /** Content to wrap (button, link, etc.) */
  children: React.ReactNode;
  /** Optional className for the wrapper */
  className?: string;
}

/** Merges Redux permissions into user object for permission checks */
function getUserWithPermissions(user: User, reduxPermissions: unknown): User {
  const basePermissions: UserPermissions = user?.permissions ?? {};
  let merged: UserPermissions = { ...basePermissions };

  if (Array.isArray(reduxPermissions)) {
    // Backward compatibility: plain array of permission codes
    merged.permissionCodes = Array.from(
      new Set([...(merged.permissionCodes ?? []), ...reduxPermissions])
    );
  } else if (
    typeof reduxPermissions === "object" &&
    reduxPermissions !== null
  ) {
    // New shape: full UserPermissions object from Redux (from /auth/users/{id}/permissions)
    const perms = reduxPermissions as UserPermissions;
    merged = {
      ...merged,
      ...perms,
      permissionCodes: Array.from(
        new Set([
          ...(merged.permissionCodes ?? []),
          ...(perms.permissionCodes ?? []),
        ])
      ),
    };
  }

  return { ...user, permissions: merged };
}

/**
 * Wraps permission-sensitive CTAs (add, create, update, delete buttons).
 * Disables the child when the user lacks the required permission.
 */
export const PermissionWrapper: React.FC<PermissionWrapperProps> = ({
  permission,
  anyOfPermissions,
  hideWhenDisabled = false,
  children,
  className = "",
}) => {
  const { user, permissions } = useSelector(
    (state: { Login: { user: User; permissions?: unknown } }) => state.Login
  );

  const userWithPermissions = getUserWithPermissions(
    (user ?? {}) as User,
    permissions
  );

  const hasAccess = permission
    ? hasPermission(userWithPermissions, permission)
    : anyOfPermissions
      ? hasAnyPermission(userWithPermissions, anyOfPermissions)
      : false;

  if (hideWhenDisabled && !hasAccess) {
    return null;
  }

  if (!hasAccess) {
    return (
      <div
        className={`permission-wrapper permission-wrapper--disabled ${className}`.trim()}
        title="You don't have permission to perform this action"
      >
        <div className="permission-wrapper__content">{children}</div>
      </div>
    );
  }

  return <>{children}</>;
};

